/**
 * A tree ring: one circle's band on a merged parent (docs/CIRCLES.md §2, D3-a).
 *
 * `ringBand` hands out an annulus just inside the ring line — a few percent of
 * the radius deep, band 0 hard against the line — and this draws it as what a
 * tree ring looks like: the circle's colour washed across the annulus at low
 * alpha, a hairline separating it from its neighbour, and, where the band is
 * wide enough to hold it, the circle's name on a short arc at the top.
 *
 * It is deliberately not a filled disc. The bands of a merged circle stack into
 * one thin belt on the year line; the calendar underneath stays a calendar.
 *
 * The wash is also the circle's hit surface — the one place a circle is drawn
 * on the ring is where it can be clicked, hovered and dropped on. The hairlines
 * and the name sit on top of it and take no pointer of their own, so a press
 * anywhere in the annulus lands on the same element.
 *
 * With one band there is nothing to tell apart, so nothing is drawn: a merged
 * circle holding a single child looks exactly like one holding none.
 */
import { arcPath, n, svgEl } from '../../core/svg';
import type { BandCtx, Fragment } from '../types';
import { W } from './metrics';

/** A band thinner than this share of the ring is a line, not a place for a name. */
const LABEL_MIN_SHARE = 0.02;

/** `d` for a full annulus: two circles, wound so `evenodd` punches the hole. */
function annulusPath(rInner: number, rOuter: number): string {
  const circle = (r: number): string =>
    `M 0 ${n(-r)} A ${n(r)} ${n(r)} 0 1 1 0 ${n(r)} A ${n(r)} ${n(r)} 0 1 1 0 ${n(-r)} Z`;
  return `${circle(rOuter)} ${circle(rInner)}`;
}

export const bandFragment: Fragment<BandCtx> = (ctx, state) => {
  const g = svgEl('g', { 'data-frag': 'band' });
  if (ctx.bandCount <= 1) return g;

  const colour = ctx.style.colour || 'var(--ring-strong)';
  const hot = state.hover || state.selected;
  const rOuter = Math.max(0, ctx.rOuter);
  const rInner = Math.max(0, Math.min(ctx.rInner, rOuter));
  const width = rOuter - rInner;

  if (width > 0) {
    g.append(
      svgEl('path', {
        d: annulusPath(rInner, rOuter),
        'fill-rule': 'evenodd',
        fill: colour,
        stroke: 'none',
        opacity: (hot ? 0.2 : 0.1) * ctx.style.opacity,
        'pointer-events': 'fill',
      }),
    );
  }

  // A hairline at every band's outer edge, plus one closing the innermost:
  // together they are the separators between neighbouring tree rings.
  const edges = ctx.bandIndex === ctx.bandCount - 1 ? [rOuter, rInner] : [rOuter];
  for (const r of edges) {
    g.append(
      svgEl('circle', {
        cx: 0,
        cy: 0,
        r: n(r),
        fill: 'none',
        stroke: colour,
        'stroke-width': hot ? W.ring : W.hairline,
        'vector-effect': 'non-scaling-stroke',
        opacity: (hot ? 0.95 : 0.55) * ctx.style.opacity,
        'pointer-events': 'none',
      }),
    );
  }

  const name = ctx.tag?.name;
  if (name && ctx.radius > 0 && width / ctx.radius >= LABEL_MIN_SHARE) {
    const pathId = `band-arc-${ctx.ringId}-${ctx.bandIndex}`;
    // Sit the baseline near the band's inner edge: the glyphs grow outwards
    // from it, so this is what keeps the name inside its own tree ring.
    const labelR = rInner + width * 0.22;
    const defs = svgEl('defs');
    // A short arc across the top of the ring: enough room for a circle's name,
    // never enough to wrap round and meet itself.
    defs.append(
      svgEl('path', {
        id: pathId,
        fill: 'none',
        d: arcPath(labelR, -0.42, 0.42),
      }),
    );
    g.append(defs);

    const text = svgEl('text', {
      fill: colour,
      'font-size': n(Math.min(width * 0.55, ctx.radius * 0.026)),
      'letter-spacing': 3,
      opacity: (hot ? 0.95 : 0.7) * ctx.style.opacity,
      'pointer-events': 'none',
    });
    const textPath = svgEl('textPath', {
      href: `#${pathId}`,
      startOffset: '50%',
      'text-anchor': 'middle',
    });
    textPath.textContent = name.toUpperCase();
    text.append(textPath);
    g.append(text);
  }

  return g;
};
